import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Copy, Mail, MessageSquare, Check, Share2, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useSpaceStore } from '../../stores/spaceStore';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import toast from 'react-hot-toast';

export default function OnboardingInvite() {
  const navigate = useNavigate();
  const { currentSpace, members } = useSpaceStore();
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sendBoth, setSendBoth] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [emailDeliveryMode, setEmailDeliveryMode] = useState<'real' | 'mock' | null>(null);
  const [smsDeliveryMode, setSmsDeliveryMode] = useState<'real' | 'mock' | null>(null);
  const suggestedInviteeName = useMemo(
    () =>
      members.find((member) => member.role === 'member' && member.display_name.trim())?.display_name?.trim() || '',
    [members]
  );

  useEffect(() => {
    if (!currentSpace) {
      navigate('/onboarding/space');
      return;
    }
    const baseUrl = `${window.location.origin}/onboarding/join/${currentSpace.invite_token || 'demo-token'}`;
    const searchParams = new URLSearchParams();

    if (suggestedInviteeName) {
      searchParams.set('prefillName', suggestedInviteeName);
    }

    setInviteUrl(searchParams.toString() ? `${baseUrl}?${searchParams.toString()}` : baseUrl);
  }, [currentSpace, currentSpace?.invite_token, suggestedInviteeName]);

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleShare = async () => {
    try {
      const shareText = `Join my ${currentSpace?.name} space on PairTab to track expenses together!`;
      if (navigator.share) {
        await navigator.share({ title: 'Join PairTab', text: shareText, url: inviteUrl });
      } else {
        await copyInviteLink();
      }
    } catch {
      // User cancelled or share failed silently
    }
  };

  const sendEmailInvite = async () => {
    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    if (!currentSpace?.id) {
      toast.error('Space not ready yet');
      return;
    }
    setIsSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/invites/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          space_id: currentSpace.id,
          email: email.trim(),
          invitee_name: email.trim().split('@')[0] || 'Partner',
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setEmailSent(true);
        setEmailDeliveryMode(data.mock ? 'mock' : 'real');
        toast.success(
          data.mock
            ? `Demo mode only: PairTab did not send a real email to ${email}`
            : `Invite sent to ${email}`
        );
      } else {
        if (data.mock) {
          setEmailSent(true);
          setEmailDeliveryMode('mock');
          toast.success(`Demo mode only: PairTab did not send a real email to ${email}`);
        } else {
          toast.error('Failed to send email invite');
        }
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to send email invite');
    } finally {
      setIsSending(false);
    }
  };

  const sendSmsInvite = async () => {
    if (!phone.trim()) {
      toast.error('Please enter a phone number');
      return;
    }
    if (!currentSpace?.id) {
      toast.error('Space not ready yet');
      return;
    }
    setIsSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/invites/sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          space_id: currentSpace.id,
          phone: phone.trim(),
          invitee_name: phone.trim(),
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSmsSent(true);
        setSmsDeliveryMode(data.mock ? 'mock' : 'real');
        toast.success(
          data.mock
            ? `Demo mode only: PairTab did not send a real SMS to ${phone}`
            : `Invite sent to ${phone}`
        );
      } else {
        if (data.mock) {
          setSmsSent(true);
          setSmsDeliveryMode('mock');
          toast.success(`Demo mode only: PairTab did not send a real SMS to ${phone}`);
        } else {
          toast.error('Failed to send SMS invite');
        }
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to send SMS invite');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendBoth = async () => {
    if (!email.trim() || !phone.trim()) {
      toast.error('Both email and phone are required');
      return;
    }
    setIsSending(true);
    try {
      await Promise.allSettled([sendEmailInvite(), sendSmsInvite()]);
      setEmailSent(true);
      setSmsSent(true);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-6 py-8 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex-1 flex flex-col max-w-md mx-auto w-full"
      >
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-1 bg-primary rounded-full" />
            <div className="w-8 h-1 bg-primary rounded-full" />
            <div className="w-8 h-1 bg-primary rounded-full" />
            <div className="w-8 h-1 bg-primary rounded-full" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Invite Members</h1>
          <p className="text-muted">Share your space with your partner or group members</p>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto">
          {/* Invite Link */}
          <div className="bg-card rounded-2xl p-4 border border-border">
            <label className="text-sm font-medium text-muted mb-2 block">Invite Link</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={inviteUrl}
                readOnly
                className="flex-1 bg-background border border-border rounded-xl px-4 py-3 text-sm text-foreground break-all"
              />
              <button
                onClick={copyInviteLink}
                className="px-4 py-3 bg-primary text-background rounded-xl font-medium flex items-center gap-2 touch-target"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Quick Share */}
          <div className="space-y-3">
            <h3 className="font-semibold text-muted text-sm">Quick Share</h3>
            <Button
              variant="secondary"
              onClick={handleShare}
              className="w-full flex items-center gap-3 justify-center py-4"
            >
              <Share2 size={20} />
              Share via System Share
            </Button>
          </div>

          {/* Email Invite */}
          <div className="bg-card rounded-2xl p-4 border border-border">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Mail size={18} />
              Email Invite
            </h3>
            <div className="space-y-3">
              <Input
                type="email"
                label="Email Address"
                placeholder="partner@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailSent ? (
                <div
                  className={`rounded-lg px-4 py-3 text-sm ${
                    emailDeliveryMode === 'mock'
                      ? 'bg-yellow-400/10 text-yellow-400'
                      : 'bg-primary/10 text-primary'
                  }`}
                >
                  <div className="flex items-center gap-2 font-medium">
                    {emailDeliveryMode === 'mock' ? <Clock size={16} /> : <Check size={16} />}
                    <span>{emailDeliveryMode === 'mock' ? 'Demo mode only' : 'Invite sent!'}</span>
                  </div>
                  <p className="mt-1 text-xs opacity-90">
                    {emailDeliveryMode === 'mock'
                      ? 'Real email delivery is not configured yet, so nothing was sent to the inbox.'
                      : 'Your invite email is on the way.'}
                  </p>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  onClick={sendEmailInvite}
                  disabled={isSending || !email.trim()}
                  className="w-full"
                >
                  {isSending ? 'Sending...' : 'Send Email Invite'}
                </Button>
              )}
            </div>
          </div>

          {/* SMS Invite */}
          <div className="bg-card rounded-2xl p-4 border border-border">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <MessageSquare size={18} />
              SMS Invite
            </h3>
            <div className="space-y-3">
              <Input
                type="tel"
                label="Phone Number"
                placeholder="+1 (555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              {smsSent ? (
                <div
                  className={`rounded-lg px-4 py-3 text-sm ${
                    smsDeliveryMode === 'mock'
                      ? 'bg-yellow-400/10 text-yellow-400'
                      : 'bg-primary/10 text-primary'
                  }`}
                >
                  <div className="flex items-center gap-2 font-medium">
                    {smsDeliveryMode === 'mock' ? <Clock size={16} /> : <Check size={16} />}
                    <span>{smsDeliveryMode === 'mock' ? 'Demo mode only' : 'SMS sent!'}</span>
                  </div>
                  <p className="mt-1 text-xs opacity-90">
                    {smsDeliveryMode === 'mock'
                      ? 'Real SMS delivery is not configured yet, so nothing was sent to the phone.'
                      : 'Your invite text is on the way.'}
                  </p>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  onClick={sendSmsInvite}
                  disabled={isSending || !phone.trim()}
                  className="w-full"
                >
                  {isSending ? 'Sending...' : 'Send SMS Invite'}
                </Button>
              )}
            </div>
          </div>

          {/* Send Both */}
          {!emailSent && !smsSent && (
            <div className="bg-card rounded-2xl p-4 border border-border">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendBoth}
                  onChange={(e) => setSendBoth(e.target.checked)}
                  className="w-5 h-5 rounded border-border mt-0.5"
                />
                <div>
                  <span className="font-medium text-sm">Send both (Email + SMS)</span>
                  <p className="text-xs text-muted mt-1">
                    Sends the invite to both channels simultaneously
                  </p>
                </div>
              </label>
              {sendBoth && (
                <div className="mt-3 pt-3 border-t border-border/50">
                  <Button
                    onClick={handleSendBoth}
                    disabled={isSending || !email.trim() || !phone.trim()}
                    className="w-full"
                  >
                    {isSending ? 'Sending both...' : 'Send Email + SMS'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* What's Next */}
          <div className="bg-card rounded-2xl p-4 border border-border">
            <div className="flex items-start gap-3">
              <span className="text-2xl">💡</span>
              <div>
                <h4 className="font-medium mb-1">What happens next?</h4>
                <ul className="text-muted text-sm space-y-1">
                  <li>• Members join via your invite link</li>
                  <li>• All expenses sync in real-time</li>
                  <li>• Real email and SMS delivery need Resend and Twilio to be configured</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 48h Reminder */}
          <div className="bg-card rounded-2xl p-4 border border-border">
            <div className="flex items-start gap-3">
              <Clock size={20} className="text-muted mt-0.5" />
              <div>
                <h4 className="font-medium mb-1">Auto Reminders</h4>
                <p className="text-muted text-sm">
                  If members haven't joined within 48 hours, we'll automatically send a reminder to your invite recipients.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-6 space-y-3">
          <Button
            onClick={() => navigate('/')}
            className="w-full"
          >
            Continue to Home
          </Button>
          <button
            onClick={() => navigate('/')}
            className="w-full text-center text-muted text-sm hover:text-foreground touch-target"
          >
            Skip for now
          </button>
        </div>
      </motion.div>
    </div>
  );
}
